import { useMemo } from 'react';
import { FULL_ACCESS_VIEWER_VALUE } from 'common/constants/user';
import { CapTableDetailsModel } from 'api/models';

interface UseProFormaPermissionsParams {
  captableInfo?: CapTableDetailsModel | null;
  currentFirmRole?: number | null;
}

interface UseProFormaPermissionsReturn {
  canEditProForma: boolean;
  canCreateProForma: boolean;
}

/**
 * Custom hook to compute proforma cap table permissions for Full Access Viewer users.
 * 
 * @param captableInfo - The current cap table information
 * @param currentFirmRole - The current user's firm role
 * @returns Object containing canEditProForma and canCreateProForma boolean flags
 */
export const useProFormaPermissions = ({
  captableInfo,
  currentFirmRole,
}: UseProFormaPermissionsParams): UseProFormaPermissionsReturn => {
  // Compute edit permission to avoid flickering when switching cap tables
  const canEditProForma = useMemo(() => {
    if (!captableInfo || currentFirmRole == null) {
      return false;
    }
    return (
      currentFirmRole === FULL_ACCESS_VIEWER_VALUE &&
      !captableInfo.is_primary &&
      captableInfo.limited_user_access_edit_allowed
    );
  }, [captableInfo, currentFirmRole]);

  // Compute if Full Access Viewer can create pro formas (from primary cap tables)
  const canCreateProForma = useMemo(() => {
    if (!captableInfo || currentFirmRole == null) {
      return false;
    }
    return (
      currentFirmRole === FULL_ACCESS_VIEWER_VALUE &&
      captableInfo.is_primary
    );
  }, [captableInfo, currentFirmRole]);

  return {
    canEditProForma,
    canCreateProForma,
  };
};
